/** @format */

import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAppSelector } from "../../../../app/hooks";
import ApplicationYear, {
  TypeOfRequest,
} from "../../../../common/dropdowns/ApplicationYear";
import CircularLoader from "../../../../common/loaders/circularLoader";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { selectAdmin } from "../../adminSlice";
import {
  DasboardDto,
  emptyYearOfStudyCounts,
  StatusDto,
  YearOfStudyCountsDto,
} from "../../models";
import ReturningApplicantsModal from "./ReturningApplicantsModal";

function formatAcademicYearSpan(intakeYear: number) {
  return `${intakeYear - 1}/${String(intakeYear).slice(-2)}`;
}

function getStatusCount(statusList: StatusDto[], key: string) {
  const norm = key.replace(/\s/g, "").toLowerCase();
  const found = statusList.find(
    (s) => s.name.replace(/\s/g, "").toLowerCase() === norm
  );
  return found?.value ?? 0;
}

function sumStatusCounts(statusList: StatusDto[], keys: string[]) {
  return keys.reduce((sum, key) => sum + getStatusCount(statusList, key), 0);
}

/** Matches `ApplicationStages` string from API (`ApplicationStage.ToString()`). */
const PENDING_UNIVERSITY_SELECTION_STAGE =
  "ApplicationFullyAssessedAndIsPendingUniversitySelection";

function normalizeYearCounts(x?: YearOfStudyCountsDto | null): YearOfStudyCountsDto {
  if (!x) return emptyYearOfStudyCounts();
  return {
    firstYear: x.firstYear ?? 0,
    secondYear: x.secondYear ?? 0,
    thirdYear: x.thirdYear ?? 0,
    fourthYear: x.fourthYear ?? 0,
    pgda: x.pgda ?? 0,
    unknown: x.unknown ?? 0,
  };
}

interface ApplicationStatusDetailItem {
  label: string;
  labelTooltip?: string;
  value: number;
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
}

function pctVsPrior(
  current: number,
  prior: number | undefined
): { pct: number; up: boolean } | null {
  if (prior === undefined) return null;
  if (prior === 0) {
    if (current === 0) return { pct: 0, up: true };
    return { pct: 100, up: true };
  }
  const raw = ((current - prior) / prior) * 100;
  const pct = Math.round(raw);
  return { pct, up: pct >= 0 };
}

interface ApplicationStatusReportProps {
  onReset?: () => void;
  dashboardYear: number;
  onDashboardYearChange?: (year: number) => void;
  variant?: "admin" | "public";
  publicPayload?: { data: DasboardDto | null; loading: boolean };
  /** Prior intake year totals for YoY trend chips */
  comparisonData?: DasboardDto | null;
  onRefresh?: () => void;
  lastRefreshedAt?: Date | null;
}

export default function ApplicationStatusReport({
  onReset,
  dashboardYear,
  onDashboardYearChange,
  variant = "admin",
  publicPayload,
  comparisonData,
  onRefresh,
  lastRefreshedAt,
}: ApplicationStatusReportProps) {
  const theme = useTheme();
  const adminAction = useAppSelector(selectAdmin);
  const [data, setData] = useState<DasboardDto>();
  const [returningModalOpen, setReturningModalOpen] = useState(false);

  const publicIntakeYearChoices = useMemo(() => {
    const base = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => base - i);
  }, []);

  useEffect(() => {
    if (variant === "public") {
      setData(publicPayload?.data ?? undefined);
      return;
    }
    if (adminAction.dashboardState.dashboard) {
      setData(adminAction.dashboardState.dashboard);
    }
  }, [variant, publicPayload?.data, adminAction.dashboardState.dashboard]);

  const loaderStatus =
    variant === "public"
      ? publicPayload?.loading
        ? "loading"
        : "idle"
      : adminAction.dashboardState.status === "loading" &&
          !adminAction.dashboardState.dashboard
        ? "loading"
        : "idle";

  const insights = useMemo(() => {
    if (!data) return null;
    const all = data.allApplications || 1;
    const completed = data.allApplications - data.totalInCompleted;
    const submitted = getStatusCount(data.status, "ApplicationSubmitted");
    const successful = getStatusCount(data.status, "ApplicationSuccessful");
    const assessedRate = data.totalAssessed / all;
    const completionRate = completed / all;
    const submissionRate = submitted / all;
    const approvalRate = successful / all;
    const totalGender = data.femaleApplicants + data.maleApplicants;
    const femaleShare =
      totalGender > 0 ? data.femaleApplicants / totalGender : 0;
    const underReview = sumStatusCounts(data.status, [
      "ApplicationInReview",
      "ApplicationSentForFurtherReview",
      PENDING_UNIVERSITY_SELECTION_STAGE,
    ]);
    return {
      completionPct: Math.round(completionRate * 100),
      submissionPct: Math.round(submissionRate * 100),
      assessedPct: Math.round(assessedRate * 100),
      approvalPct: Math.round(approvalRate * 100),
      femalePct: Math.round(femaleShare * 100),
      submitted,
      completed,
      successful,
      underReview,
    };
  }, [data]);

  const priorTotals = useMemo(() => {
    if (!comparisonData) return null;
    return {
      allApplications: comparisonData.allApplications,
      totalInCompleted: comparisonData.totalInCompleted,
      totalAssessed: comparisonData.totalAssessed,
    };
  }, [comparisonData]);

  const applicationStatusDetailRows = useMemo(() => {
    if (!data) {
      return { row1: [] as ApplicationStatusDetailItem[], row2: [] as ApplicationStatusDetailItem[] };
    }
    const row1: ApplicationStatusDetailItem[] = [
      {
        label: "All",
        value: data.allApplications,
        icon: <AppsOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.primary.main, 0.12),
        iconColor: theme.palette.primary.main,
      },
      {
        label: "Application Submitted",
        value: getStatusCount(data.status, "ApplicationSubmitted"),
        icon: <SendOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.success.main, 0.12),
        iconColor: theme.palette.success.dark,
      },
      {
        label: "Documents Upload",
        value: getStatusCount(data.status, "DocumentsUpload"),
        icon: <UploadFileOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.info.main, 0.14),
        iconColor: theme.palette.info.dark,
      },
      {
        label: "Address Details",
        value: getStatusCount(data.status, "AddressDetails"),
        icon: <LocationOnOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha("#5c6bc0", 0.14),
        iconColor: "#3949ab",
      },
      {
        label: "Institution Details",
        value: getStatusCount(data.status, "InstitutionDetails"),
        icon: <SchoolOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.warning.main, 0.16),
        iconColor: theme.palette.warning.dark,
      },
      {
        label: "Legal Guardian Details",
        value: getStatusCount(data.status, "LegalGuardianDetails"),
        icon: <FamilyRestroomOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha("#7b1fa2", 0.12),
        iconColor: "#6a1b9a",
      },
      {
        label: "Pending University Selection",
        labelTooltip:
          "Application fully assessed and pending university selection",
        value: getStatusCount(data.status, PENDING_UNIVERSITY_SELECTION_STAGE),
        icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.secondary.main, 0.14),
        iconColor: theme.palette.secondary.dark,
      },
      {
        label: "Assessed",
        value: getStatusCount(data.status, "Assessed"),
        icon: <FactCheckOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha("#00897b", 0.14),
        iconColor: "#00695c",
      },
    ];
    const row2: ApplicationStatusDetailItem[] = [
      {
        label: "Incomplete",
        labelTooltip: "Applications without a completion date",
        value: data.totalInCompleted,
        icon: <WarningAmberOutlinedIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.warning.main, 0.14),
        iconColor: theme.palette.warning.dark,
      },
      {
        label: "Female",
        value: data.femaleApplicants,
        icon: <PersonOutlineIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha("#c2185b", 0.12),
        iconColor: "#ad1457",
      },
      {
        label: "Male",
        value: data.maleApplicants,
        icon: <PersonIcon sx={{ fontSize: 26 }} />,
        iconBg: alpha(theme.palette.primary.main, 0.12),
        iconColor: theme.palette.primary.dark,
      },
    ];
    return { row1, row2 };
  }, [data, theme]);

  const returningLabel = `${dashboardYear - 1}–${dashboardYear}`;
  const refreshedLabel = lastRefreshedAt
    ? lastRefreshedAt.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

  const surfaceSx = {
    bgcolor: "grey.50",
    borderRadius: 3,
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    border: `1px solid ${theme.palette.divider}`,
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            Application pipeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
            Track application progress and key metrics across the bursary cycle.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          {variant === "admin" && onReset && (
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              size="small"
            >
              Reset year
            </Button>
          )}
          {variant === "public" ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarMonthIcon sx={{ color: "text.secondary", fontSize: 22 }} />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="public-dashboard-year-label">
                  Academic year
                </InputLabel>
                <Select
                  labelId="public-dashboard-year-label"
                  id="public-dashboard-year-select"
                  label="Academic year"
                  value={
                    publicIntakeYearChoices.includes(dashboardYear)
                      ? String(dashboardYear)
                      : String(publicIntakeYearChoices[0] ?? dashboardYear)
                  }
                  onChange={(e: SelectChangeEvent<string>) => {
                    const y = Number(e.target.value);
                    if (Number.isFinite(y)) onDashboardYearChange?.(y);
                  }}
                >
                  {publicIntakeYearChoices.map((y) => (
                    <MenuItem key={y} value={String(y)}>
                      {formatAcademicYearSpan(y)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CalendarMonthIcon sx={{ color: "text.secondary", fontSize: 22 }} />
              <ApplicationYear
                request={TypeOfRequest.LoadDashboard}
                selectedYear={dashboardYear}
                onYearChange={(y) => onDashboardYearChange?.(y)}
              />
            </Stack>
          )}
        </Stack>
      </Stack>

      <CircularLoader status={loaderStatus} />

      {data && insights && (
        <Box sx={surfaceSx}>
        <Stack spacing={3} sx={{ mb: 0 }}>
          <Grid container spacing={2}>
            {variant === "admin" && (onRefresh || lastRefreshedAt) && (
              <Grid item xs={12} lg={3}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  <CardContent sx={{ py: 2, px: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label="NEW"
                        color="primary"
                        sx={{ fontWeight: 800, letterSpacing: 0.5, height: 22 }}
                      />
                      <InfoOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Data refreshed
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      {refreshedLabel}
                    </Typography>
                    {onRefresh && (
                      <Tooltip title="Refresh data">
                        <IconButton
                          size="small"
                          onClick={onRefresh}
                          color="primary"
                          aria-label="Refresh dashboard"
                          className="no-print"
                          sx={{
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} sm={6} lg={variant === "admin" && (onRefresh || lastRefreshedAt) ? 3 : 4}>
              <TrendChip
                title="Applications"
                value={data.allApplications}
                trend={pctVsPrior(
                  data.allApplications,
                  priorTotals?.allApplications
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={variant === "admin" && (onRefresh || lastRefreshedAt) ? 3 : 4}>
              <TrendChip
                title="Incomplete"
                value={data.totalInCompleted}
                trend={pctVsPrior(
                  data.totalInCompleted,
                  priorTotals?.totalInCompleted
                )}
                invertColors
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={variant === "admin" && (onRefresh || lastRefreshedAt) ? 3 : 4}>
              <TrendChip
                title="Assessed"
                value={data.totalAssessed}
                trend={pctVsPrior(
                  data.totalAssessed,
                  priorTotals?.totalAssessed
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(
                      theme.palette.secondary.main,
                      0.88
                    )} 100%)`,
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ShowChartIcon sx={{ opacity: 0.95 }} />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Key rates
                    </Typography>
                  </Stack>
                </Box>
                <CardContent sx={{ py: 2.5 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Application completion rate
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {insights.completionPct}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {insights.completed} of {data.allApplications} completed
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={insights.completionPct}
                        sx={{
                          mt: 1,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Assessment rate
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {insights.assessedPct}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.totalAssessed} assessed
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={insights.assessedPct}
                        sx={{
                          mt: 1,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.2
                          ),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                            backgroundColor: theme.palette.secondary.main,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Approval rate
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {insights.approvalPct}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {insights.successful} approved of{" "}
                        {data.allApplications}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={insights.approvalPct}
                        sx={{
                          mt: 1,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: alpha(theme.palette.info.main, 0.2),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                            backgroundColor: "#00897b",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2.5,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: alpha("#7b1fa2", 0.12),
                        color: "#7b1fa2",
                        display: "flex",
                      }}
                    >
                      <GroupsOutlinedIcon />
                    </Box>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Returning students
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Students who applied in the previous academic year
                        {variant === "admin"
                          ? " and reapplied for this intake."
                          : "."}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ color: "#6a1b9a", lineHeight: 1 }}
                  >
                    {data.returningApplicants.toLocaleString()}
                  </Typography>
                  {variant === "admin" && (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ChevronRightIcon />}
                      onClick={() => setReturningModalOpen(true)}
                      sx={{
                        alignSelf: "flex-start",
                        fontWeight: 600,
                        borderColor: alpha("#7b1fa2", 0.5),
                        color: "#7b1fa2",
                        "&:hover": {
                          borderColor: "#7b1fa2",
                          bgcolor: alpha("#7b1fa2", 0.04),
                        },
                      }}
                    >
                      View list
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<DescriptionOutlinedIcon fontSize="small" />}
                  label="Applications"
                  value={data.allApplications}
                  color={theme.palette.primary.main}
                  yearBreakdown={normalizeYearCounts(
                    data.applicationsByYearOfStudy
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<PendingActionsOutlinedIcon fontSize="small" />}
                  label="Incomplete"
                  value={data.totalInCompleted}
                  color={theme.palette.warning.main}
                  yearBreakdown={normalizeYearCounts(
                    data.incompleteByYearOfStudy
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<TaskAltOutlinedIcon fontSize="small" />}
                  label="Submitted"
                  value={insights.submitted}
                  color={theme.palette.success.main}
                  yearBreakdown={normalizeYearCounts(
                    data.submittedByYearOfStudy
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<RateReviewOutlinedIcon fontSize="small" />}
                  label="Under review"
                  value={insights.underReview}
                  color="#5c6bc0"
                  yearBreakdown={normalizeYearCounts(
                    data.underReviewByYearOfStudy
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<FactCheckOutlinedIcon fontSize="small" />}
                  label="Assessed"
                  value={data.totalAssessed}
                  color={theme.palette.primary.main}
                  yearBreakdown={normalizeYearCounts(
                    data.assessedByYearOfStudy
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatMini
                  icon={<VerifiedOutlinedIcon fontSize="small" />}
                  label="Approved"
                  value={insights.successful}
                  color={theme.palette.success.dark}
                  yearBreakdown={normalizeYearCounts(
                    data.approvedByYearOfStudy
                  )}
                />
              </Grid>
            </Grid>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="subtitle1"
                component="h2"
                fontWeight={700}
                sx={{ mt: 0.5, mb: 1.5, color: "primary.dark" }}
              >
                Application status
              </Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(4, 1fr)",
                      md: "repeat(4, 1fr)",
                      lg: "repeat(8, 1fr)",
                    },
                  }}
                >
                  {applicationStatusDetailRows.row1.map((item) => (
                    <ApplicationStatusDetailCard
                      key={item.label}
                      item={item}
                    />
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                    },
                  }}
                >
                  {applicationStatusDetailRows.row2.map((item) => (
                    <ApplicationStatusDetailCard
                      key={item.label}
                      item={item}
                    />
                  ))}
                </Box>
              </Stack>
            </Box>
        </Stack>
        </Box>
      )}

      {variant === "admin" && (
        <ReturningApplicantsModal
          open={returningModalOpen}
          onClose={() => setReturningModalOpen(false)}
          intakeYear={dashboardYear}
          cohortLabel={returningLabel}
        />
      )}
    </>
  );
}

function ApplicationStatusDetailCard({
  item,
}: {
  item: ApplicationStatusDetailItem;
}) {
  const theme = useTheme();
  const card = (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        bgcolor: "grey.100",
        border: `1px solid ${alpha(theme.palette.divider, 0.95)}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s ease, transform 0.15s ease",
        "&:hover": {
          boxShadow: `0 8px 24px ${alpha(item.iconColor, 0.14)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent
        sx={{
          py: 2,
          px: 1.25,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: item.iconBg,
            color: item.iconColor,
          }}
        >
          {item.icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "primary.dark",
            fontWeight: 600,
            lineHeight: 1.3,
            display: "block",
          }}
        >
          {item.label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: "primary.dark", lineHeight: 1.1 }}
        >
          {item.value.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );

  if (item.labelTooltip) {
    return (
      <Tooltip title={item.labelTooltip} arrow placement="top">
        <Box sx={{ height: "100%" }}>{card}</Box>
      </Tooltip>
    );
  }

  return card;
}

function PipelineYearInsightLegend({ counts }: { counts: YearOfStudyCountsDto }) {
  const theme = useTheme();
  const rows: { label: string; value: number }[] = [
    { label: "1st year", value: counts.firstYear },
    { label: "2nd year", value: counts.secondYear },
    { label: "3rd year", value: counts.thirdYear },
    { label: "4th year", value: counts.fourthYear },
    { label: "PGDA", value: counts.pgda },
  ];
  return (
    <Box
      sx={{
        mt: 1.25,
        pt: 1.25,
        borderTop: `1px dashed ${alpha(theme.palette.divider, 0.95)}`,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        display="block"
        sx={{ mb: 0.75, letterSpacing: 0.2 }}
      >
        By year of study
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          columnGap: 0.75,
          rowGap: 0.25,
        }}
      >
        {rows.map((r) => (
          <Fragment key={r.label}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              {r.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              textAlign="right"
              sx={{ lineHeight: 1.35 }}
            >
              {r.value.toLocaleString()}
            </Typography>
          </Fragment>
        ))}
        {counts.unknown > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              Other / not captured
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              textAlign="right"
              sx={{ lineHeight: 1.35 }}
            >
              {counts.unknown.toLocaleString()}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

function TrendChip({
  title,
  value,
  trend,
  invertColors,
}: {
  title: string;
  value: number;
  trend: { pct: number; up: boolean } | null;
  invertColors?: boolean;
}) {
  const theme = useTheme();

  let trendSection: ReactNode;
  if (trend !== null) {
    const effectiveUp = invertColors ? !trend.up : trend.up;
    const trendColor = effectiveUp
      ? theme.palette.success.main
      : theme.palette.error.main;
    const Icon = effectiveUp ? ArrowUpwardIcon : ArrowDownwardIcon;
    trendSection = (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
        <Icon sx={{ fontSize: 20, color: trendColor }} />
        <Typography variant="body2" fontWeight={700} sx={{ color: trendColor }}>
          {trend.pct >= 0 ? "+" : ""}
          {trend.pct}%
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            fontWeight={400}
            sx={{ ml: 0.5 }}
          >
            vs prior year
          </Typography>
        </Typography>
      </Stack>
    );
  } else {
    trendSection = (
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.75 }}
      >
        Prior-year comparison unavailable
      </Typography>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        height: "100%",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.15 }}>
          {value.toLocaleString()}
        </Typography>
        {trendSection}
      </CardContent>
    </Card>
  );
}

function StatMini({
  icon,
  label,
  value,
  color,
  subtitle,
  yearBreakdown,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  color: string;
  subtitle?: string;
  yearBreakdown?: YearOfStudyCountsDto;
}) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.2s, transform 0.15s",
        "&:hover": {
          boxShadow: `0 8px 24px ${alpha(color, 0.12)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ py: 1.75, px: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ color, display: "flex" }}>{icon}</Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.2,
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
        {yearBreakdown && <PipelineYearInsightLegend counts={yearBreakdown} />}
      </CardContent>
    </Card>
  );
}
